import {
  always,
  concat,
  cond,
  equals,
  compose,
  filter,
  find,
  gte,
  head,
  ifElse,
  isEmpty,
  isNil,
  lte,
  or,
  pathOr,
  propEq,
  T,
  toUpper,
  length,
  gt,
  ascend,
  prop,
  sortWith,
  slice,
  lensPath,
  set,
  map,
  startsWith,
  endsWith
} from 'ramda';
import { formatPhoneNumber, normalizeNumber } from "src/utilities/formatNumbers"
import { YEARS_WITH_EMPLOYER } from 'src/components/CheckLists/documents';
import { ROLE_BORROWER, ROLE_COBORROWER, YES } from 'src/constants';
import { useApplicantManagement } from 'src/hooks/useAuthenticatedContext';
import useLoanApp from 'src/hooks/useLoanAppHook';
import {
  BORROWER,
  COBORROWER,
  DEEDSY_SH_BORROWER,
  DEEDSY_SH_COBORROWER,
  DEEDSY_SH_MERCHANT_CASH_ADVANCES,
  DIVORCED_UPPERCASE_D,
  DIVORCED_UPPERCASE_D_ONLY,
  FEMALE_LOWERCASE,
  FEMALE_UPPERCASE,
  FEMALE_UPPERCASE_F,
  FEMALE_UPPERCASE_F_ONLY,
  KIND_LOANAPP_ROLE,
  LABELS,
  MALE_LOWERCASE,
  MALE_UPPERCASE,
  MALE_UPPERCASE_M,
  MALE_UPPERCASE_M_ONLY,
  MARRIED_UPPERCASE_M,
  MARRIED_UPPERCASE_M_ONLY,
  META_DATA,
  NEUTRAL_LOWERCASE,
  NEUTRAL_UPPERCASE,
  OBJECT_META,
  SEPARATED_UPPERCASE_S,
  SEPARATED_UPPERCASE_S_ONLY,
  UNMARRIED_UPPERCASE_U,
  UNMARRIED_UPPERCASE_U_ONLY,
  WIDOWED_UPPERCASE_W,
  WIDOWED_UPPERCASE_W_ONLY,
  YES_DOWNCASE,
  KIND_LOANAPP,
} from 'src/services/constants';
import {
  getLabelValue,
  setOwnerReference,
  setLabel,
} from 'src/services/objects/build';
import {
  findOwnerReferenceOfKind,
  findOwnerReferenceOfUid,
} from 'src/services/objects/finder';
import {
  addressShortener,
  addressStringified,
} from 'src/utilities/addressJoiner';
import { buildLabel, firstName, lastName, middleName } from './naming';
import { ObjectMeta, OwnerReference } from 'src/types';
import { getNamespaceName } from 'src/services/objects/namespaces';
import { DEEDSY_SH_HOME_PHONE_NUMBER, DEEDSY_SH_CELL_PHONE_NUMBER, AT_EMAIL_COM } from 'src/features/constants';
import { isNotEmpty } from './hasValue';
import { LoanRole } from 'src/types/Loan';

/**
 *
 * getTheBorrower? Why not use the filter method with label DEEDSY_SH_BORRWER?
 *
 * Returns a single borrower labelled DEEDSY_SH_BORROWER from an array of borrower records
 *
 * @param borrowers { object } All the borrowers array. These record can be
 * have role  borrower or coborrower.
 *
 */
export const getTheBorrower = (borrowers: any) => {
  return head(filter(isBorrower, borrowers)) || {};
};

/**
 *
 * getTheCoBorrower? Why not use the filter method with label DEEDSY_SH_COBORRWER?
 *
 * Returns a single coborrower labelled DEEDSY_SH_COBORRWER from an array of borrower records
 *
 * @param borrowers { object } All the borrowers array. These record can be
 * have role  borrower or coborrower.
 *
 */

export const getTheCoBorrower = (allBorrowers: any) => {
  return filter(isCoborrower, allBorrowers) || [];
};

const isBorrower = (n: any) =>
  equals(
    pathOr('', [OBJECT_META, LABELS, DEEDSY_SH_BORROWER], n),
    YES_DOWNCASE
  );

const isCoborrower = (n: any) =>
  equals(
    pathOr('', [OBJECT_META, LABELS, DEEDSY_SH_COBORROWER], n),
    YES_DOWNCASE
  );

export const orderBorrowers = (borrower: any) => {
  const b = getTheCoBorrower(borrower);
  const a = getTheBorrower(borrower);
  // FIXME: For BC or business loan the lendsmart_sh/borrower: yes in the loan role was missing
  //  when the deals were imported. We need the API to automatically set the owner label
  //    in the imported loan to have lendsmart_sh/borrower: yes. Untill then
  //    this is a temporary fix
  return gt(length(borrower), 1) ? concat([a], b) : borrower;
};

/**
 *
 * stringifyTheBorrower
 *
 * Returns a stringified role name (Borrower or Coborrower) for the borrower record.
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
export const stringifyTheBorrower = (borrower: any) =>
  cond([
    [isBorrower, always(BORROWER)],
    [isCoborrower, always(COBORROWER)],
    [T, () => BORROWER],
  ])(borrower);

/**
 *
 * getNameFromTheBorrower
 *
 * Returns the borrower name for the given borrower record.
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
export const getName = (borrower: any) => pathOr('', ['profile', 'name'], borrower);

/**
 *
 * getIdFromTheBorrower
 *
 * Returns the id for the given borrower or co-borrower record.
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or co-borrower.
 *
 */
const getId = (borrower: any) => uniqueId(borrower);

/**
 *
 * getAddressFromTheBorrower
 *
 * Returns an address  for the given borrower record.
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 * @param status
 */
const getAddress = (borrower: any, status?: any) =>
  pathOr({}, ['addresses', status ? status : 'present'], borrower);

const getPresentAddressType = (borrower: any, status?: any) =>
  pathOr('', ['addresses', 'present', 'owner_ship'], borrower);

/**
 *
 * stringifyTheAddress
 *
 * Returns the address stringified concat using commas.
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
const stringifyTheAddress = (borrower: any) =>
  addressStringified(getAddress(borrower));

/* *
 *
 * cityAndStateShortener
 *
 * Returns the city and state from address and separate them using commas.

 * */

const cityAndStateShortener = (borrower: any, status: any) =>
  addressShortener(getAddress(borrower, status));
/**
 *
 * getAccount
 *
 * Returns an account id   for the given borrower record.
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
const getAccount = (borrower: any) =>
  pathOr('', [OBJECT_META, 'account'], borrower);

/**
 *
 * getDOB
 *
 * Returns the date of birth address stringified concat using commas.
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
const getDOB = (borrower: any) =>
  pathOr('', ['profile', 'date_of_birth'], borrower);

/**
 *
 * getGender
 *
 * Returns the sex of the borrower record
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
const getGender = (borrower: any) => pathOr('', ['profile', 'sex'], borrower);

/**
 *
 * getSSN
 *
 * Returns the social security number of the borrower record
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
export const getSSN = (borrower: any) =>
  pathOr('', ['profile', 'social_security_no'], borrower);

/**
 *
 * getMartialStatus
 *
 * Returns the marital status (raw form)
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
const getMartialStatus = (borrower: any) =>
  pathOr('', ['profile', 'marital_status'], borrower);

/**
 *
 * getPhoneNumber
 *
 * Returns the home phone number of the borrower record
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
const getPhoneNumber = (borrower: any) =>
  pathOr('', ['profile', 'home_phone'], borrower);

/**
 *
 * humanizeMartialStatus
 *
 * Returns the humanied marital status of the borrower record.
 *
 *
 * @param borrower { object } The single borrower record. This record can be
 * have role  borrower or coborrower.
 *
 */
const humanizeBorrowerMartialStatus = (borrower: any) => {
  return humanizeMartialStatus(
    pathOr('', ['profile', 'marital_status'], borrower)
  );
};

export const humanizeMartialStatus = (ms: string) => {
  return cond([
    [equals(MARRIED_UPPERCASE_M_ONLY), always(MARRIED_UPPERCASE_M)],
    [equals(UNMARRIED_UPPERCASE_U_ONLY), always(UNMARRIED_UPPERCASE_U)],
    [equals(SEPARATED_UPPERCASE_S_ONLY), always(SEPARATED_UPPERCASE_S)],
    [equals(DIVORCED_UPPERCASE_D_ONLY), always(DIVORCED_UPPERCASE_D)],
    [equals(WIDOWED_UPPERCASE_W_ONLY), always(WIDOWED_UPPERCASE_W)],
  ])(toUpper(ms));
};

/**
 *
 * humanizeGender
 *
 * Returns the humanized gender which is lowercased
 *
 *
 * @param gender { string } The crude gender as stored as sex in the profle of
 * the single borrower record.
 *
 */
export const humanizeGender = (gender: any) => {
  return cond([
    [equals(MALE_UPPERCASE_M_ONLY), always(MALE_LOWERCASE)],
    [equals(MALE_UPPERCASE), always(MALE_LOWERCASE)],
    [equals(MALE_UPPERCASE_M), always(MALE_LOWERCASE)],
    [equals(FEMALE_UPPERCASE_F_ONLY), always(FEMALE_LOWERCASE)],
    [equals(FEMALE_UPPERCASE), always(FEMALE_LOWERCASE)],
    [equals(FEMALE_UPPERCASE_F), always(FEMALE_LOWERCASE)],
    [equals(NEUTRAL_UPPERCASE), always(NEUTRAL_LOWERCASE)],
    [T, () => ''],
  ])(toUpper(gender));
};

/**
 *
 * public method: humanizedBorrowerOf
 *
 * Returns the humanized borrower from an array of brrower record. In this case
 * filter and truly return the first occurence (head) labelled borrower.
 *
 * @param borrowers { object } All the borrowers array. These record can be
 * have role  borrower or coborrower.
 *
 */

// The unique id of an API data object
export const uniqueId = (data: any) => {
  return pathOr('', ['id'], data);
};

const getRoles = (loanApp: any) => pathOr([], ['spec', 'loanapp_roles'], loanApp);


// getTheResourceAccountId get the object_meta account id of given api resource
export const getTheResourceAccountId = (data: any) => {
  return pathOr('', ['object_meta', 'account'], data);
};

//
export const getTheBorrowerRoleId = (thisLoanApp: any) => {
  return uniqueId(
    getTheBorrowerLoanAppRole(
      pathOr([], ['spec', 'loanapp_roles'], thisLoanApp)
    )
  );
};

export const getTheMerchantRoleId = (thisLoanApp: any) => {
  return uniqueId(
    getTheMerchantLoanAppRole(
      pathOr([], ['spec', 'loanapp_roles'], thisLoanApp)
    )
  );
};

export const humanizeBorrowerOf = (borrowers: any, roleId: string) => {
  const b = roleId
    ? head(getTheResourceByRoleId(borrowers, roleId))
    : getTheBorrower(borrowers);
  return humanized(b);
};

/**
 *
 * getTheResourceByRoleId
 *
 * Returns a single borrower has owner reference id
 *
 * @param resources { object, roleId } All the resources array api resouce object and owner ref roleId. These record can be
 * have role  borrower or coborrower.
 *
 * @param roleId
 */
export const getTheResourceByRoleId = (resources: any, roleId: string) => {
  return filter(
    (item: any) =>
      !!roleId && !isNil(findOwnerReferenceOfUid(item, roleId)),
    sortWith([ascend(prop("created_at"))])(resources)
  );
};

export const getUserAccounts = (loanApp: any) => pathOr([], ['spec', 'user_accounts'], loanApp)

const getTheResourceByUid = (
  resources: any,
  uid: string
) => filter((b: any) => equals(uniqueId(b), uid), resources);


export const getTheAccountByUid = (
  userAccounts: any,
  accountId: string
) => getTheResourceByUid(userAccounts, accountId)

/**
 *
 * public method: getTheResourceByAccountId
 *
 * Returns the api resource object which match with resource's object_meta account
 * filter and truly return the first occurence (head).
 *
 * @param resources { object } List of api resources. These record can be
 * have role borrower or coborrower.
 *
 */

export const getTheResourceByAccountId = (
  resources: any,
  accountId: string
) => filter((b: any) => equals(getTheResourceAccountId(b), accountId), resources);

export const getTheCoBorrowerRoleId = (thisLoanApp: any) => {
  return uniqueId(
    getTheCoBorrowerLoanAppRole(thisLoanApp)
  );
};

export const getTheBorrowerAccountId = (thisLoanApp: any) => {
  return getTheResourceAccountId(
    getTheBorrowerLoanAppRole(
      pathOr([], ['spec', 'loanapp_roles'], thisLoanApp)
    )
  );
};

export const getTheCoBorrowerAccountId = (thisLoanApp: any) => {
  return getTheResourceAccountId(
    getTheCoBorrowerLoanAppRole(thisLoanApp)
  );
};

export const getFormerAddress = (address: any) =>
  pathOr({}, ['addresses', 'former', 0], address);

export const humanized = (b: any) => {
  return {
    id: getId(b),
    rolestr: stringifyTheBorrower(b),
    name: getName(b),
    account: getAccount(b),
    dob: getDOB(b),
    addrstr: stringifyTheAddress(b),
    addrJson: getAddress(b),
    addrShortener: cityAndStateShortener(b, 'present'),
    formerAddr: getAddress(b, 'former'),
    formerAddrShortener: cityAndStateShortener(b, 'former'),
    humanizeGender: humanizeGender(getGender(b)),
    humanizeMartialStatus: humanizeBorrowerMartialStatus(b),
    phoneno: getPhoneNumber(b),
    ssn: getSSN(b),
    maritalStatus: getMartialStatus(b),
    borrowerAddressType: getPresentAddressType(b),
    mailingAddress: getAddress(b, 'mailing_address'),
    mailingAddressStr: addressStringified(getAddress(b, 'mailing_address')),
    formerAddressStr: addressStringified(getFormerAddress(b)),
    firstName: firstName(pathOr({}, ['profile'], b)),
    middleName: middleName(pathOr({}, ['profile'], b)),
    lastName: lastName(pathOr({}, ['profile'], b))
  };
};

/**
 *
 * public method: humanizedCoBorrowerOf
 *
 * Returns the humanized coborrower from an array of brrower record. In this case
 * filter and truly return the first occurence (head) labelled coborrower.
 *
 * @param borrowers { object } All the borrowers array. These record can be
 * have role  borrower or coborrower.
 *
 */
export const humanizedCoBorrowerOf = (borrowers: any) => {
  const b = getTheCoBorrower(borrowers);
  return humanized(b);
};

/**
 *
 * public method: filterTheResourcesByRole
 *
 * Returns a subset of resource records representing either borrower or
 * coborrower for the given resource records.
 *
 *
 * @param resources { object } All the borrowers array. These record can be
 * have role  borrower or coborrower.
 *
 * @param loanRoleStr { string }  A string of representing the role we want to
 *                               filter.
 *
 */
export const filterTheResourcesByRole = (
  resources: any[],
  loanRole: LoanRole
) => {
  const hasKindLabelled = (d: any) =>
    equals(getLabelValue(d, equals(loanRole, ROLE_COBORROWER) ? DEEDSY_SH_COBORROWER: DEEDSY_SH_BORROWER), YES_DOWNCASE);
  return filter(hasKindLabelled, isNotEmpty(resources) ? resources : []);
};

/**
 *
 * public method: filterTheBorrowersById
 *
 * Returns a subset of borrower record representing either borrower or
 * coborrower for the given borrower records and the loanRoleId.
 *
 *
 * @param borrowers { object } All the borrowers array. These record can be
 * have role  borrower or coborrower.
 *
 * @param loanRoleId { string }  A string representing the loan role id to
 * filter
 *
 */
export const filterTheBorrowersById = (
  borrowers: any[] = [],
  loanRoleId: string
) => {
  return head(
    ifElse(
      (b: any) => or(isNil(b), isEmpty(b)),
      always([]),
      (d: any) =>
        filter(
          (f: any) =>
            equals(findOwnerReferenceOfKind(f, KIND_LOANAPP_ROLE), loanRoleId),
          d
        )
    )(borrowers)
  );
};



export const getHomePhoneNumber = (n: any) => {
  const homePhoneNumber = pathOr([], ['schema_set'], n);

  return head(
    filter(propEq('name', DEEDSY_SH_HOME_PHONE_NUMBER), homePhoneNumber)
  );
};

export const getCellPhoneNumber = (n: any) => {
  const homePhoneNumber = pathOr([], ['schema_set'], n);

  return head(
    filter(propEq('name', DEEDSY_SH_CELL_PHONE_NUMBER), homePhoneNumber)
  );
};

export const getYearOfworking = (n: any) => {
  const yearOfworking = pathOr([], ['schema_set'], n);

  return head(filter(propEq('name', YEARS_WITH_EMPLOYER), yearOfworking));
};

export const isBorrowerLoanAppRole = (n: any) => {
  return (
    equals(pathOr('', [META_DATA, 'name'], n), DEEDSY_SH_BORROWER) ||
    equals(pathOr('', [OBJECT_META, LABELS, DEEDSY_SH_BORROWER], n), 'yes')
  );
};

export const isCoborrowerLoanAppRole = (n: any) =>
  equals(pathOr('', [META_DATA, 'name'], n), DEEDSY_SH_COBORROWER) ||
  equals(pathOr('', [OBJECT_META, LABELS, DEEDSY_SH_COBORROWER], n), 'yes');

// we have to change this
const isMerchantLoanAppRole = (n: any) =>
  equals(pathOr('', [META_DATA, 'name'], n), DEEDSY_SH_MERCHANT_CASH_ADVANCES);

/**TODO:
  This is patch to show standard borrower pair until we enable borrower pair switch option on URLA page **/
export const getTheCoBorrowerLoanAppRole = (thisLoanApp: any) => {
  return head(getTheCoBorrowerLoanAppRoles(pathOr([], ['spec', 'loanapp_roles'], thisLoanApp)));
};

export const getTheBorrowerLoanAppRole = (loanAppRoles: []) => {
  return head(filter(isBorrowerLoanAppRole, loanAppRoles));
};

export const getTheCoBorrowerLoanAppRoles = (loanAppRoles: []) => {
  return filter(isCoborrowerLoanAppRole,
    sortWith([ascend(prop("created_at"))])(loanAppRoles));
};

export const getTheMerchantLoanAppRole = (loanAppRoles: []) => {
  return head(filter(isMerchantLoanAppRole, loanAppRoles));
};

export const acceptPhoneNumber = (value: any) => {
  const phnNum = normalizeNumber(value);
  const len = phnNum.length;
  return !!equals(len, 10);
};
export const ssnFormatter = (value: any, onSetSSN: any) => {
  const ssnNum = normalizeNumber(value);
  const len = ssnNum.length;
  if (!equals(Number(ssnNum), 0) && lte(len, 9)) {
    onSetSSN(value);
  } else if (equals(Number(ssnNum), 0)) {
    onSetSSN('');
  }
};
export const acceptSSN = (value: any) => {
  return !!equals(value.length, 11);
};

export const phoneNumberFormatter = (value: any) => {
  const changeNum = lte(length(value), 14) ? normalizeNumber(value) : value;
  const len = length(changeNum);
  if (!equals(Number(changeNum), 0) && lte(len, 10)) {
    return formatPhoneNumber(value);
  } else if (equals(Number(changeNum), 0)) {
    return '';
  } else if (gte(len, 14)) {
    return slice(0, 14, value);
  }
  return ''
};

// TODO: we should remove this method since its using full loanApp
export const filterSpecificLoanRole = () => {
  const { loanRoleId } = useApplicantManagement();
  const { loanapp_roles } = useLoanApp();
  return head(getTheResourceByUid(loanapp_roles, loanRoleId));
};

export const stringifyTheLoanRole = (loanRole: any) =>
  cond([
    [isBorrowerLoanAppRole, always(DEEDSY_SH_BORROWER)],
    [isCoborrowerLoanAppRole, always(DEEDSY_SH_COBORROWER)],
    [isMerchantLoanAppRole, always(DEEDSY_SH_MERCHANT_CASH_ADVANCES)],
    [T, () => DEEDSY_SH_BORROWER],
  ])(loanRole);

// TODO some places we have used DEEDSY_SH_BORRWER and some ROLE_BORROWER
// so it is
export const convertRoleTextWithoutLabel = (role: string) => {
  return cond([
    [equals(DEEDSY_SH_BORROWER), always(ROLE_BORROWER)],
    [equals(DEEDSY_SH_COBORROWER), always(ROLE_COBORROWER)],
    [(e: string) => isNotEmpty(e), always(role)],
    [T, () => ROLE_BORROWER],
  ])(role);
}

export const getProfileDetails = (profiles: any, name: string) =>
  filter(
    (item: any) =>
      equals(
        pathOr('', ['object_meta', 'labels', buildLabel(name)], item),
        YES
      ),
    profiles
  );

export const getResourceById = (
  resources: any,
  uid: string
) =>
  find(
    (item: any) => equals(uniqueId(item), uid),
    resources
  );

export const getTheAccountByEmail = (
  userAccounts: any,
  email: string
) =>
  find(
    (item: any) => equals(pathOr('', ['email'], item), email),
    userAccounts
  );

export const getBorrowerByRoleId = (borrowers: any, roleId: any) => {
  const data = filter((e: any) => {
    return equals(roleId, pathOr('', ['loanrole_id'], e));
  }, borrowers);
  return head(data);
};


/**
 *
 * public method: getLoanRoleByAccount
 *
 * Returns a loanapp_role api resource which resource's id match with given loanRoleId
 *
 * @param loanApp { object } loan bag which contains full loan app data
 * @param account { string } account id of the loanapp_role which role data we need to fetch and return
 * returns loanRole or undefined if there is no match found
 */

export const getLoanRoleByAccount = (loanApp: any, accountId: any) => {
  return find(
    (x: any) => equals(getAccount(x), accountId),
    getRoles(loanApp)
  );
};


/**
 *
 * public method: getLoanRoleById
 *
 * Returns a loanapp_role api resource which resource's id match with given loanRoleId
 *
 * @param loanApp { object } loan bag which contains full loan app data
 * @param loanRoleId { string } role_id id of the loanapp_role which role data we need to fetch and return
 * returns loanRole or undefined if there is no match found
 */

export const getLoanRoleById = (loanApp: any, loanRoleId: any) => {
  const loanapp_roles = getRoles(loanApp);
  const loanRoleobj = filter(
    (x: any) => equals(uniqueId(x), loanRoleId),
    loanapp_roles
  );
  return head(loanRoleobj);
};

/**
 *
 * public method: getLoanRoleById
 *
 * Returns a loanapp_role api resource which resource's id match with given loanRoleId
 *
 * @param loanApp { object } loan bag which contains full loan app data
 * @param email { string } email id of the loanapp_role which role data we need to fetch and return
 * returns loanRole or undefined if there is no match found
 */

export const getLoanRoleByEmail = (loanApp: any, email: any) => {
  const accountId = uniqueId(getTheAccountByEmail(
    pathOr([], ['spec', 'user_accounts'], loanApp),
    email))
  const loanRoleobj = filter(
    (x: any) => equals(getAccount(x), accountId),
    getRoles(loanApp)
  );
  return head(loanRoleobj);
};


/**
  public method: generateRandomEmail
  this method is to generate a dummy email
  * @param role { string } loanrole of the application to set prefix of an Email
  * returns email address with prefix role and current unix timestamp concat with dot (.)
*/
export const generateRandomEmail = (prefix: string) => {
  return `${prefix}.${Math.floor(Date.now() / 1000)}${AT_EMAIL_COM}`
}

export const isRandomEmail = (prefix: string, email: string) => {
  return isNotEmpty(email) && startsWith(prefix,email) && endsWith(AT_EMAIL_COM, email)
}


export const mkObjectForLoanAppRole = (
  name: string,
  loanApp: any,
  loanRoleId?: any,
  loanData?: any
) => {
  const loanRoleAccountId = pathOr('', ['loanRoleAccountId'], loanData)
  const loanAppId = pathOr('', ['loanAppId'], loanData)
  // By default we take borrower role id if we don't send loanRoleId
  const loanStatus = pathOr({}, ['spec', 'loan_status', 0], loanApp);
  const loanId = loanAppId || pathOr('', ['id'], loanApp);
  const account_id = loanRoleAccountId || pathOr('', ['object_meta', 'account'], loanApp);
  const activeRoleId = loanRoleId || pathOr('', ['loanrole_id'], loanStatus);
  const loanRole = stringifyTheLoanRole(getLoanRoleById(loanApp, loanRoleId));
  const owner_references: OwnerReference[] = [];
  const object: ObjectMeta = {
    name,
    account: account_id,
    namespace: getNamespaceName(),
    owner_references,
  };
  const { object_meta } = compose(
    setLabel(loanRole ? loanRole : "", YES_DOWNCASE),
    setOwnerReference(loanId, KIND_LOANAPP),
    setOwnerReference(activeRoleId, KIND_LOANAPP_ROLE)
  )({ object_meta: object });

  return object_meta
}

export const mkObjectForMonthlyIncomeRole = (
  name: string,
  _loanRole: string,
  employmentId: string,
  loanApp?: any,
  loanRoleId?: any,
) => {
  const { object_meta } = compose(
    setOwnerReference(employmentId, 'Employment')
  )({ object_meta: mkObjectForLoanAppRole(name, loanApp, loanRoleId) });
  return object_meta
};

export const mkLoanAppObject = (data: any, loanAppId: any, loanRoleId: any) => {
  const objectMeta = isNotEmpty(pathOr({}, ['borrower', 'object_meta'], data)) ? 
  pathOr({}, ['borrower', 'object_meta'], data) :  pathOr({}, ['business', 'object_meta'], data)
  const loanAppRoles = [
    {
      id: loanRoleId,
      object_meta: objectMeta
    }
  ]
  const result = compose(
    set(lensPath(['id']), loanAppId),
    set(lensPath(['object_meta']), objectMeta),
    set(lensPath(['spec', 'loanapp_roles']), loanAppRoles)
  )({});
  return result
}

export const getBorrowersEmail = (borrowers:any) =>{
    return map((borrower:any)=>pathOr('',['profile','email'],borrower) ,borrowers)
}